import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Star, ShoppingBag, Heart, ShieldCheck, Truck, ArrowLeft, 
  Sparkles, Check, ChevronRight, Feather, ShieldAlert, Award, PackageCheck 
} from 'lucide-react';
import { products } from '../data/products';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import ProductCard from '../components/product/ProductCard';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { wishlist, toggleWishlist } = useWishlist();

  const product = products.find((p) => String(p.id) === String(id)) || products[0];
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('specs');
  const [addedNotification, setAddedNotification] = useState(false);

  // Safely gather multi-images or fallback to primary product image
  const productImages = product?.images?.length
    ? product.images
    : [
        product?.image,
        'https://images.unsplash.com/photo-1610030469983-98e550d6193c?auto=format&fit=crop&q=80&w=1000',
        'https://images.unsplash.com/photo-1617627143750-d86bc21e42bb?auto=format&fit=crop&q=80&w=1000'
      ].filter(Boolean);

  const [activeImage, setActiveImage] = useState(productImages[0]);

  // Sync active image and reset quantity when route product changes
  useEffect(() => {
    if (productImages[0]) {
      setActiveImage(productImages[0]);
    }
    setQuantity(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id, product]);

  const isWishlisted = wishlist.some((item) => item?.id === product?.id);

  // Helper function to format INR currency
  const formatRupees = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedNotification(true);
    setTimeout(() => setAddedNotification(false), 3000);
  };

  // Recommended related products (safely comparing category)
  const relatedProducts = products
    .filter((p) => String(p.category) === String(product?.category) && String(p.id) !== String(product?.id))
    .slice(0, 4);

  // Safe category label string conversion helper
  const categoryLabel = String(product?.category || 'Collection').replace(/-/g, ' ');

  // Determine if item is jewelry or saree for tailored specs display
  const isJewelry = String(product?.category || '').toLowerCase().includes('jewelry') || 
                    String(product?.category || '').toLowerCase().includes('sets');

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 bg-[#FDFBF7]">
      {/* 1. BREADCRUMB & BACK NAVIGATION */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center space-x-2 text-xs font-serif font-bold text-[#7A6B5D] hover:text-[#4A0E17] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Collection</span>
        </button>

        <div className="hidden sm:flex items-center space-x-2 text-[11px] font-serif text-[#7A6B5D]">
          <span>Vedhyas</span>
          <ChevronRight className="w-3 h-3 text-[#E8DFC8]" />
          <span className="capitalize">{categoryLabel}</span>
          <ChevronRight className="w-3 h-3 text-[#E8DFC8]" />
          <span className="text-[#4A0E17] font-bold truncate max-w-[180px]">{product?.name}</span>
        </div>
      </div>

      {/* 2. HERO PRODUCT SHOWCASE CONTAINER */}
      <div className="bg-[#FDFBF7] rounded-3xl p-6 sm:p-10 border border-[#E8DFC8] shadow-md grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 relative overflow-hidden">
        
        {/* Ambient Burgundy Soft Glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#4A0E17]/5 rounded-full blur-3xl pointer-events-none" />

        {/* LEFT COLUMN: IMAGE GALLERY */}
        <div className="lg:col-span-7 flex flex-col-reverse sm:flex-row gap-4">
          
          {/* Thumbnails Strip */}
          <div className="flex sm:flex-col gap-3 overflow-x-auto sm:overflow-visible scrollbar-none pb-2 sm:pb-0">
            {productImages.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiveImage(img)}
                className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl overflow-hidden border-2 transition-all flex-shrink-0 bg-[#F4EFE6] ${
                  activeImage === img
                    ? 'border-[#4A0E17] ring-2 ring-[#4A0E17]/20 shadow-sm'
                    : 'border-[#E8DFC8] hover:border-[#4A0E17]/50'
                }`}
              >
                <img src={img} alt={`${product?.name} view ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>

          {/* Main Hero Image */}
          <div className="relative flex-1 h-[400px] sm:h-[540px] rounded-2xl overflow-hidden bg-[#F4EFE6] border border-[#E8DFC8] group">
            <img
              src={activeImage}
              alt={product?.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            />

            {/* Collection Tag */}
            <span className="absolute top-4 left-4 bg-[#FDFBF7]/90 backdrop-blur-md text-[#4A0E17] text-[10px] font-serif font-bold px-3 py-1 rounded-full uppercase tracking-wider border border-[#E8DFC8] shadow-sm">
              {categoryLabel}
            </span>

            {/* Wishlist Heart Button */}
            <button
              onClick={() => toggleWishlist(product)}
              className={`absolute top-4 right-4 p-2.5 rounded-full backdrop-blur-md transition-all duration-300 shadow-sm ${
                isWishlisted
                  ? 'bg-[#4A0E17] text-[#FDFBF7] scale-105'
                  : 'bg-[#FDFBF7]/80 text-[#4A0E17] hover:bg-[#FDFBF7] hover:scale-110'
              }`}
            >
              <Heart className={`w-4 h-4 ${isWishlisted ? 'fill-current text-[#FDFBF7]' : 'text-[#4A0E17]'}`} />
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN: DETAILS */}
        <div className="lg:col-span-5 flex flex-col justify-between space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-1.5">
                <Star className="w-4 h-4 text-[#4A0E17] fill-current" />
                <span className="text-xs font-serif font-bold text-[#4A0E17]">{product?.rating || '4.9'}</span>
                <span className="text-xs text-[#7A6B5D] font-light">(Handcrafted Excellence)</span>
              </div>
              <span className="inline-flex items-center space-x-1 text-[10px] font-serif font-bold uppercase tracking-wider text-[#4A0E17] bg-[#4A0E17]/10 px-2.5 py-0.5 rounded-full border border-[#4A0E17]/20">
                <Award className="w-3 h-3 text-[#4A0E17]" />
                <span>Certified Pure</span>
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-[#4A0E17] tracking-tight mb-2">
              {product?.name}
            </h1>

            {(product?.fabric || product?.craft || product?.material) && (
              <p className="text-xs text-[#6B1320] font-serif font-bold tracking-wide uppercase mb-3">
                {product?.fabric || product?.material} {product?.craft ? `• ${product.craft}` : ''}
              </p>
            )}

            <div className="flex items-baseline space-x-3 mb-4 pb-4 border-b border-[#E8DFC8]">
              <span className="text-2xl sm:text-3xl font-serif font-extrabold text-[#4A0E17]">
                {formatRupees(product?.price)}
              </span>
              {product?.mrp && product.mrp > product.price && (
                <span className="text-sm text-[#7A6B5D] line-through font-normal">
                  {formatRupees(product.mrp)}
                </span>
              )}
              <span className="text-[11px] text-[#7A6B5D]">Incl. all taxes & Insured Delivery</span>
            </div>

            <p className="text-xs sm:text-sm text-[#544842] leading-relaxed font-light mb-6">
              {product?.description || 
                (isJewelry 
                  ? 'Handcrafted antique temple gold finish artwork set with semi-precious stones, designed for traditional bridal occasions.' 
                  : 'Intricately handwoven by master weavers using pure silk yarn and opulent zari work.')}
            </p>

            <div className="flex items-center space-x-4 mb-6">
              <span className="text-xs font-serif font-bold uppercase tracking-wider text-[#7A6B5D]">
                Quantity:
              </span>
              <div className="flex items-center bg-[#F4EFE6] border border-[#E8DFC8] rounded-xl overflow-hidden p-0.5">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-8 h-8 flex items-center justify-center text-xs font-bold text-[#4A0E17] hover:bg-[#FDFBF7] rounded-lg transition"
                >
                  -
                </button>
                <span className="w-10 text-center text-xs font-serif font-bold text-[#4A0E17]">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-8 h-8 flex items-center justify-center text-xs font-bold text-[#4A0E17] hover:bg-[#FDFBF7] rounded-lg transition"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t border-[#E8DFC8]">
            {addedNotification && (
              <div className="bg-emerald-50 text-emerald-800 text-xs font-serif font-bold px-4 py-2.5 rounded-xl border border-emerald-200 flex items-center space-x-2 animate-in fade-in duration-200">
                <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Added to your Vedhyas shopping bag!</span>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <button
                onClick={handleAddToCart}
                className="flex-1 bg-[#4A0E17] hover:bg-[#6B1320] text-[#FDFBF7] py-3.5 px-6 rounded-2xl text-xs font-serif font-bold uppercase tracking-wider flex items-center justify-center space-x-2 transition-all duration-300 shadow-md hover:shadow-lg active:scale-95 border border-[#6B1320]"
              >
                <ShoppingBag className="w-4 h-4" />
                <span>Add to Bag • {formatRupees((product?.price || 0) * quantity)}</span>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-4 border-t border-[#E8DFC8] text-[11px] text-[#544842]">
              <div className="flex items-center space-x-2 bg-[#F4EFE6] p-2.5 rounded-xl border border-[#E8DFC8]">
                <Truck className="w-4 h-4 text-[#4A0E17] flex-shrink-0" />
                <span className="font-serif font-bold text-[#4A0E17]">Insured Express Shipping</span>
              </div>
              <div className="flex items-center space-x-2 bg-[#F4EFE6] p-2.5 rounded-xl border border-[#E8DFC8]">
                <ShieldCheck className="w-4 h-4 text-[#4A0E17] flex-shrink-0" />
                <span className="font-serif font-bold text-[#4A0E17]">100% Handloom Authenticity</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* 3. SPECIFICATIONS & CARE TABS */}
      <div className="bg-[#FDFBF7] rounded-3xl p-6 sm:p-10 border border-[#E8DFC8] shadow-sm mb-16">
        <div className="flex space-x-6 border-b border-[#E8DFC8] pb-4 mb-6 overflow-x-auto scrollbar-none">
          <button
            onClick={() => setActiveTab('specs')}
            className={`inline-flex items-center space-x-2 text-xs font-serif font-bold uppercase tracking-wider pb-2 border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'specs'
                ? 'border-[#4A0E17] text-[#4A0E17]'
                : 'border-transparent text-[#7A6B5D] hover:text-[#4A0E17]'
            }`}
          >
            <PackageCheck className="w-4 h-4" />
            <span>{isJewelry ? 'Dimensions & Weight' : 'Dimensions & Blouse Piece'}</span>
          </button>

          <button
            onClick={() => setActiveTab('materials')}
            className={`inline-flex items-center space-x-2 text-xs font-serif font-bold uppercase tracking-wider pb-2 border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'materials'
                ? 'border-[#4A0E17] text-[#4A0E17]'
                : 'border-transparent text-[#7A6B5D] hover:text-[#4A0E17]'
            }`}
          >
            <Feather className="w-4 h-4" />
            <span>{isJewelry ? 'Plating & Stones' : 'Weave & Zari Details'}</span>
          </button>

          <button
            onClick={() => setActiveTab('care')}
            className={`inline-flex items-center space-x-2 text-xs font-serif font-bold uppercase tracking-wider pb-2 border-b-2 whitespace-nowrap transition-all ${
              activeTab === 'care'
                ? 'border-[#4A0E17] text-[#4A0E17]'
                : 'border-transparent text-[#7A6B5D] hover:text-[#4A0E17]'
            }`}
          >
            <ShieldAlert className="w-4 h-4" />
            <span>Care & Preservation</span>
          </button>
        </div>

        <div className="text-xs sm:text-sm text-[#544842] leading-relaxed">
          {activeTab === 'specs' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {isJewelry ? (
                <>
                  <div className="bg-[#F4EFE6] p-4 rounded-2xl border border-[#E8DFC8]">
                    <span className="text-[10px] uppercase font-serif font-bold text-[#7A6B5D] block mb-1">Chain/Necklace Length</span>
                    <p className="font-serif font-bold text-[#4A0E17]">Adjustable Dori / 18 Inches</p>
                  </div>
                  <div className="bg-[#F4EFE6] p-4 rounded-2xl border border-[#E8DFC8]">
                    <span className="text-[10px] uppercase font-serif font-bold text-[#7A6B5D] block mb-1">Set Contents</span>
                    <p className="font-serif font-bold text-[#4A0E17]">1 Necklace + 1 Pair Earrings</p>
                  </div>
                  <div className="bg-[#F4EFE6] p-4 rounded-2xl border border-[#E8DFC8]">
                    <span className="text-[10px] uppercase font-serif font-bold text-[#7A6B5D] block mb-1">Total Weight</span>
                    <p className="font-serif font-bold text-[#4A0E17]">Approx. 180g – 240g</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-[#F4EFE6] p-4 rounded-2xl border border-[#E8DFC8]">
                    <span className="text-[10px] uppercase font-serif font-bold text-[#7A6B5D] block mb-1">Saree Length</span>
                    <p className="font-serif font-bold text-[#4A0E17]">5.5 Meters</p>
                  </div>
                  <div className="bg-[#F4EFE6] p-4 rounded-2xl border border-[#E8DFC8]">
                    <span className="text-[10px] uppercase font-serif font-bold text-[#7A6B5D] block mb-1">Unstitched Blouse Piece</span>
                    <p className="font-serif font-bold text-[#4A0E17]">Included (0.8 Meters)</p>
                  </div>
                  <div className="bg-[#F4EFE6] p-4 rounded-2xl border border-[#E8DFC8]">
                    <span className="text-[10px] uppercase font-serif font-bold text-[#7A6B5D] block mb-1">Total Weight</span>
                    <p className="font-serif font-bold text-[#4A0E17]">Approx. 650g – 850g</p>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'materials' && (
            <p className="font-light">
              {isJewelry 
                ? 'crafted in high-grade brass alloy with premium 22K antique gold polish, embedded with Kemp ruby pink, emerald green, and Kundan style stones.'
                : 'Handcrafted with pure Mulberry Silk warp and weft yarn, interwoven with tested zari brocade motifs across the pallu and body.'}
            </p>
          )}

          {activeTab === 'care' && (
            <p className="font-light">
              {isJewelry
                ? 'Keep away from water, perfumes, and direct sprays. Store in airtight pouches after gently wiping with a soft cotton velvet cloth.'
                : 'Dry clean only to preserve natural silk sheen and delicate gold threadwork. Store wrapped in a soft muslin cloth in a cool, dry area.'}
            </p>
          )}
        </div>
      </div>

      {/* 4. CURATED PAIRING RECOMMENDATIONS */}
      {relatedProducts.length > 0 && (
        <section className="mb-12">
          <div className="flex items-center space-x-2 mb-2">
            <Sparkles className="w-3.5 h-3.5 text-[#4A0E17]" />
            <span className="text-[10px] font-serif font-bold tracking-[0.25em] text-[#4A0E17] uppercase">
              Curated Pairing
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-serif font-bold text-[#4A0E17] tracking-tight mb-6">
            Complete The Look
          </h2>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {relatedProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetailPage;